import { toast } from '@qse/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import {
  completeProfilePictureUpload,
  deleteProfilePicture,
  getProfilePicture,
  startProfilePictureUpload,
} from '../file-management'
import { queryKeys, queryOptions } from '../react-query-config'
import type {
  CompleteProfilePictureUploadDto,
  CompleteProfilePictureUploadResponseDto,
  DeleteProfilePictureResponseDto,
  GetProfilePictureResponseDto,
  StartProfilePictureUploadDto,
  StartProfilePictureUploadResponseDto,
} from '../types'

// Flag to track if we just completed an upload (used to show success toast on next query success)
let justUploadedProfilePicture = false

// Hook for getting profile picture
export const useProfilePicture = (options?: { enabled?: boolean; tenantStatus?: string }) => {
  const { enabled, tenantStatus, ...restOptions } = options || {}

  // Check if tenant status prevents API calls
  const isTenantStatusBlocked =
    tenantStatus && ['incomplete', 'suspended', 'cancelled'].includes(tenantStatus)

  return useQuery<GetProfilePictureResponseDto, Error>({
    queryKey: queryKeys.profilePicture.current(),
    queryFn: getProfilePicture,
    enabled: (enabled ?? true) && !isTenantStatusBlocked,
    ...queryOptions.auth, // Use auth query options since it's user-related data
    ...restOptions,
  })
}

// Hook for uploading profile picture
export const useUploadProfilePicture = () => {
  const queryClient = useQueryClient()

  return useMutation<
    CompleteProfilePictureUploadResponseDto,
    Error,
    { file: File; onProgress?: (progress: number) => void }
  >({
    mutationFn: async ({ file, onProgress }) => {
      // Validate file before starting upload
      const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
      const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

      if (file.size > MAX_FILE_SIZE) {
        throw new Error('Image too large. Please select an image smaller than 20MB.')
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        throw new Error('Invalid file type. Please select a JPG, PNG, WebP, or GIF image.')
      }

      // Step 1: Start the upload process
      onProgress?.(10)
      const startResponse = await startProfilePictureUpload({
        mimeType: file.type,
        sizeBytes: file.size,
        fileName: file.name,
      })

      // Step 2: Upload to S3 using the signed URL
      onProgress?.(30)
      const uploadResponse = await fetch(startResponse.signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3')
      }

      onProgress?.(70)

      // Step 3: Complete the upload process
      const completeResponse = await completeProfilePictureUpload({
        uploadId: startResponse.uploadId,
      })

      onProgress?.(100)

      return completeResponse
    },
    onSuccess: data => {
      // Verify the response contains a valid profile picture URL
      if (!data?.profilePictureUrl) {
        toast.error('Upload completed but profile picture URL not received. Please try again.')

        return
      }

      // Set flag to show success toast when profile picture query succeeds (from WebSocket event)
      justUploadedProfilePicture = true

      // Directly update the query cache with the upload response data
      // This avoids an unnecessary GET request since we already have the profile picture URL
      // The WebSocket event will trigger a refetch later when thumbnails are ready
      queryClient.setQueryData(queryKeys.profilePicture.current(), {
        profilePicture: {
          uuid: data.user.id, // Use user ID as temporary identifier
          updatedAt: new Date().toISOString(),
          urls: {
            original: data.profilePictureUrl,
          },
          processing: data.processing,
        },
      } satisfies GetProfilePictureResponseDto)

      // Invalidate user data to update the user object
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
    },
    onError: error => {
      toast.error(error.message || 'Failed to upload profile picture. Please try again.')
    },
  })
}

// Hook for deleting profile picture
export const useDeleteProfilePicture = () => {
  const queryClient = useQueryClient()

  return useMutation<DeleteProfilePictureResponseDto, Error, void>({
    mutationFn: deleteProfilePicture,
    onSuccess: () => {
      // Directly set the profile picture data to null to avoid additional GET request
      queryClient.setQueryData(queryKeys.profilePicture.current(), null)

      // Invalidate user data to update the user object
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })

      toast.success('Profile picture removed successfully!')
    },
    onError: () => {
      toast.error('Failed to delete profile picture. Please try again.')
    },
  })
}

// Composite hook that provides all profile picture functionality
export const useProfilePictureManager = (options?: {
  enabled?: boolean
  tenantStatus?: string
}) => {
  const profilePictureQuery = useProfilePicture(options)
  const uploadMutation = useUploadProfilePicture()
  const deleteMutation = useDeleteProfilePicture()

  // Track if we've shown the success toast for the current data
  const hasShownToastForDataRef = useRef(false)

  // Show success toast when profile picture data is fetched after an upload
  // Only show when processing is complete (i.e., when WebSocket event triggers the fetch with thumbnails ready)
  useEffect(() => {
    const profilePicture = profilePictureQuery.data?.profilePicture

    if (
      justUploadedProfilePicture &&
      profilePictureQuery.isSuccess &&
      profilePicture &&
      !profilePicture.processing && // Only show toast when thumbnails are ready
      !hasShownToastForDataRef.current
    ) {
      toast.success('Profile picture updated successfully!')
      justUploadedProfilePicture = false // Reset the flag
      hasShownToastForDataRef.current = true
    }

    // Reset toast flag when starting a new upload
    if (uploadMutation.isPending) {
      hasShownToastForDataRef.current = false
    }
  }, [profilePictureQuery.isSuccess, profilePictureQuery.data, uploadMutation.isPending])

  return {
    // Query data
    profilePicture: profilePictureQuery.data,
    isLoading: profilePictureQuery.isLoading,
    isError: profilePictureQuery.isError,
    error: profilePictureQuery.error,

    // Upload functionality
    uploadProfilePicture: uploadMutation.mutate,
    uploadProfilePictureAsync: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    uploadError: uploadMutation.error,

    // Delete functionality
    deleteProfilePicture: deleteMutation.mutate,
    deleteProfilePictureAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,

    // Utility methods
    refetch: profilePictureQuery.refetch,
    reset: () => {
      uploadMutation.reset()
      deleteMutation.reset()
    },
  }
}

/**
 * Lightweight hook for components that only need to display profile picture
 * Uses the same cached query as useProfilePictureManager but with minimal overhead
 */
export const useProfilePictureDisplay = (options?: {
  enabled?: boolean
  tenantStatus?: string
}) => {
  const profilePictureQuery = useProfilePicture(options)

  return {
    profilePicture: profilePictureQuery.data,
    isLoading: profilePictureQuery.isLoading,
    isError: profilePictureQuery.isError,
    error: profilePictureQuery.error,
  }
}

// Export types for external use
export type {
  CompleteProfilePictureUploadDto,
  CompleteProfilePictureUploadResponseDto,
  DeleteProfilePictureResponseDto,
  GetProfilePictureResponseDto,
  StartProfilePictureUploadDto,
  StartProfilePictureUploadResponseDto,
}
